// "Tell an agent what to do..." -> the planner's plan card -> Deploy.
//
// The words go to the planner (usePlanRequest: one request at a time, 90 s at most, late answers
// dropped), and the plan card it answers with takes the box's place. The card's Cancel comes back to
// the box with the words kept; Deploy empties the box and hands the new agent to onCreated.
//
// The chart: `patient` is the open chart's row (its id and patient_name) and `schema` the schema it
// lives in, as the clinic page has it. With "This patient" on, the planner gets {schema, id, label}
// with that real schema. With no schema known the chip isn't offered at all: a chart is never sent
// under a made-up schema. The card keeps the chart its plan was made for, whatever is open later.
//
// Errors show under the box: the server's sentence (a code put into words), with Retry for a passing
// failure (a 503 that isn't "switched off", the time limit, a dropped connection).
//
// A view-as session can't plan (the planner refuses it): the box is off, and says why.
//
// A question: when the request doesn't say which messages it means (patients' texts, or the
// Messages between staff in NotesAI), the planner answers with a question (planner.QUESTION_MESSAGES)
// instead of a plan, or with one beside its plan. The question takes the box's place first: each
// option adds its sentence to the words, which are planned again; Cancel comes back to the box.
//
// A patient's details never stay beside another patient's chart: when another chart opens (or none),
// a plan card made for the last chart's patient closes, and a request still waiting for one stops,
// with a neutral line. Words typed into the box name no chart, and stay; so does a plan with no chart.
//
// Props: patient, schema, types (for the plan card), disabled (agents are switched off on this server),
// onCreated(agent).
import React, { useEffect, useRef, useState } from 'react';
import PlanCard from './PlanCard';
import usePlanRequest from './usePlanRequest';
import { isImpersonating } from '../../../utils/impersonation';

// The characters the words use that aren't ASCII, so this file stays ASCII.
const ELLIPSIS = String.fromCharCode(0x2026);
const TICK = String.fromCharCode(0x2713);
// What the planner takes (views.MAX_INSTRUCTION); the server refuses more with its own sentence.
const MAX_INSTRUCTION = 4000;

const BOX = 'm-3 rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900/40 p-2.5 space-y-2';
const INPUT = 'w-full resize-none bg-transparent text-xs text-slate-800 dark:text-slate-100 placeholder:text-slate-400 dark:placeholder:text-slate-500 outline-none disabled:opacity-60';
const CHIP = 'px-2 py-0.5 rounded-full text-[11px] font-semibold disabled:opacity-50';
const CHIP_ON = 'bg-blue-100 dark:bg-blue-500/15 text-blue-700 dark:text-blue-300';
const CHIP_OFF = 'bg-slate-100 dark:bg-slate-800/60 text-slate-500 dark:text-slate-400';
const BUTTON = 'rounded-md px-3 py-1.5 text-xs font-semibold';
const PLAN = 'bg-blue-600 hover:bg-blue-500 disabled:opacity-50 text-white';
const QUIET = 'text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800';
const NOTE = 'text-[11px] text-slate-500 dark:text-slate-400';
const LEFT_CHART = 'A plan for the chart you left was closed.';
const ERROR = 'flex items-start justify-between gap-2 text-[11px] text-red-600 dark:text-red-400';
const RETRY = 'shrink-0 rounded-md px-2 py-0.5 text-[11px] font-semibold text-red-700 dark:text-red-300 hover:bg-red-100 dark:hover:bg-red-500/10';

// Which chart a chart is: its schema and id.
const keyOf = (chart) => (chart ? JSON.stringify([chart.schema, chart.id]) : null);

// The chart an agent can be tied to, as the planner takes it, or null.
function chartOf(patient, schema) {
  if (!patient || patient.id === null || patient.id === undefined || String(patient.id) === '') return null;
  if (typeof schema !== 'string' || !schema.trim()) return null;
  return { schema, id: String(patient.id), label: typeof patient.patient_name === 'string' ? patient.patient_name : '' };
}

export default function NewAgentBox({ patient = null, schema = null, types = [], disabled = false, onCreated }) {
  const [text, setText] = useState('');
  const [withPatient, setWithPatient] = useState(true);
  const [planned, setPlanned] = useState(null); // {answer, chart}: the plan card, and the chart it was made for
  const [notice, setNotice] = useState(null); // a neutral line: what was closed when the chart changed
  const { planning, error, request, cancel } = usePlanRequest();
  const chart = chartOf(patient, schema);
  // Entering and leaving a view-as session both load the page afresh, so this can't change under it.
  const viewAs = isImpersonating();
  const off = disabled || viewAs;
  const chartKey = keyOf(chart);
  const openFor = useRef(chartKey); // the chart open when the box last looked
  const askedFor = useRef(null); // the chart of the request in flight, if it has one

  // Another chart is open now, or none: what was made for the last chart's patient goes.
  useEffect(() => {
    if (openFor.current === chartKey) return;
    openFor.current = chartKey;
    let closed = false;
    if (planned?.chart) {
      setPlanned(null);
      closed = true;
    }
    if (planning && askedFor.current) {
      cancel();
      closed = true;
    }
    setNotice(closed ? LEFT_CHART : null);
  }, [chartKey, planned, planning, cancel]);

  const plan = async (instruction) => {
    if (!instruction || planning || off) return;
    const forChart = withPatient ? chart : null;
    askedFor.current = keyOf(forChart);
    setNotice(null);
    const answer = await request({ instruction, patient: forChart });
    if (answer) setPlanned({ answer, chart: forChart });
  };
  const submit = (event) => {
    event?.preventDefault?.();
    return plan(text.trim());
  };
  // An answer to the planner's question: its sentence joins the words, which are planned again.
  const answerQuestion = (option) => {
    const instruction = `${text.trim()} ${option.adds}`.trim();
    setText(instruction);
    return plan(instruction);
  };

  const question = planned?.answer?.question;
  if (question && Array.isArray(question.options)) {
    return (
      <section aria-label="The planner's question" className={BOX}>
        <div className="text-xs font-semibold text-slate-800 dark:text-slate-100">{question.text}</div>
        <div className={NOTE}>
          {"\"Messages\" can mean patients' texts or the Messages between staff in NotesAI, and an agent works differently for each."}
        </div>
        <div className="flex flex-col gap-1.5">
          {question.options.map((option) => (
            <button key={option.label} type="button" disabled={planning} onClick={() => answerQuestion(option)}
                    className={`${BUTTON} ${QUIET} text-left border border-slate-200 dark:border-slate-700`}>
              {option.label}
            </button>
          ))}
        </div>
        <div className="flex items-center justify-between gap-2">
          {planning ? <span role="status" className={NOTE}>{`Planning${ELLIPSIS}`}</span> : <span />}
          <button type="button" onClick={() => { cancel(); setPlanned(null); }} className={`${BUTTON} ${QUIET}`}>Cancel</button>
        </div>
        {error && (
          <div role="alert" className={ERROR}>
            <span>{error.text}</span>
          </div>
        )}
      </section>
    );
  }
  if (planned) {
    return (
      <PlanCard plan={planned.answer} types={types} patient={planned.chart} onCancel={() => setPlanned(null)}
                onDeployed={(agent) => { setPlanned(null); setText(''); onCreated?.(agent); }} />
    );
  }
  return (
    <form onSubmit={submit} aria-label="New agent" className={BOX}>
      <textarea aria-label="Tell an agent what to do" rows={3} maxLength={MAX_INSTRUCTION} disabled={off || planning}
                placeholder={`Tell an agent what to do${ELLIPSIS} e.g. check the fax inbox for CLINIC_A tomorrow morning and summarize it here`}
                className={INPUT} value={text} onChange={(e) => setText(e.target.value)} />
      <div className="flex items-center justify-between gap-2">
        {chart ? (
          <button type="button" aria-pressed={withPatient} disabled={off || planning}
                  onClick={() => setWithPatient((on) => !on)} className={`${CHIP} ${withPatient ? CHIP_ON : CHIP_OFF}`}>
            This patient{withPatient && <span aria-hidden="true">{` ${TICK}`}</span>}
          </button>
        ) : <span />}
        <div className="flex items-center gap-1.5">
          {planning && <button type="button" onClick={cancel} className={`${BUTTON} ${QUIET}`}>Cancel</button>}
          <button type="submit" disabled={off || planning || !text.trim()} className={`${BUTTON} ${PLAN}`}>
            {planning ? `Planning${ELLIPSIS}` : 'Plan it'}
          </button>
        </div>
      </div>
      {viewAs && <div className={NOTE}>{"You're viewing as someone else, so you can't plan agents."}</div>}
      {notice && <div role="status" className={NOTE}>{notice}</div>}
      {error && (
        <div role="alert" className={ERROR}>
          <span>{error.text}</span>
          {error.retry && <button type="button" onClick={submit} className={RETRY}>Retry</button>}
        </div>
      )}
    </form>
  );
}
